/**
 * Fictoan Button Component Prop Schema
 * Static schema based on Fictoan v2.1.0 Button component
 * Maps Fictoan props to shadcn/ui equivalents
 */

// Fictoan Button Props (from Fictoan v2.1.0)

const TailwindColors = [
    "slate",
    "gray",
    "zinc",
    "neutral",
    "stone",
    "red",
    "orange",
    "amber",
    "yellow",
    "lime",
    "green",
    "emerald",
    "teal",
    "cyan",
    "sky",
    "blue",
    "indigo",
    "violet",
    "purple",
    "fuchsia",
    "pink",
    "rose",
    "setu-brand",
] as const;

type TailwindColorsType = (typeof TailwindColors)[number];

const CustomColors = ["setu-brand"] as const;

type CustomColours = (typeof CustomColors)[number];

type supportedShadesType = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950;

const ShadowsMapping = {
    none: "",
    mild: "setu:shadow-xs",
    soft: "setu:shadow-md",
    hard: "setu:shadow-lg",
} as const;

const SizeMapping = {
    tiny: "icon",
    small: "sm",
    medium: "default",
    large: "lg",
    huge: "lg", // Map huge to lg since shadcn doesn't have xl for buttons
} as const;

const ShapeMapping = {
    rounded: "setu:rounded-md",
    curved: "setu:rounded-xl",
} as const;

const SpacingPaddingMapping = {
    none: "setu:p-0",
    nano: "setu:p-1",
    micro: "setu:p-2",
    tiny: "setu:p-3",
    small: "setu:p-4",
    medium: "setu:p-6",
    large: "setu:p-8",
    huge: "setu:p-12",
} as const;

const SpacingMarginMapping = {
    none: "setu:m-0",
    nano: "setu:m-1",
    micro: "setu:m-2",
    tiny: "setu:m-3",
    small: "setu:m-4",
    medium: "setu:m-6",
    large: "setu:m-8",
    huge: "setu:m-12",
} as const;

export type ShapeType = keyof typeof ShapeMapping;
export type SizeType = keyof typeof SizeMapping;
export type ShadowType = keyof typeof ShadowsMapping;
export type SpacingPaddingType = keyof typeof SpacingPaddingMapping;
export type SpacingMarginType = keyof typeof SpacingMarginMapping;

export interface FictoanButtonProps {
    // Colors
    colour?: "blue" | "red" | "green" | "yellow" | "orange" | "pink" | "purple" | "grey" | "white" | "black";
    color?: "blue" | "red" | "green" | "yellow" | "orange" | "pink" | "purple" | "grey" | "white" | "black";

    // Button types/kinds
    kind?: "primary" | "secondary" | "tertiary" | "custom";

    // Sizes - support both Fictoan and shadcn/ui size values
    size?: SizeType | "sm" | "default" | "lg" | "icon" | "icon-sm" | "icon-lg" | null;

    // States
    isLoading?: boolean;
    disabled?: boolean;

    // Visual enhancements
    shadow?: ShadowType;

    // Layout

    isFullWidth?: boolean;

    // Common HTML props
    onClick?: () => void;
    type?: "button" | "submit" | "reset";
    children?: React.ReactNode;
}

// Mapping from Fictoan to shadcn/ui
export const fictoanButtonMapping = {
    // Color to variant mapping
    colors: {
        blue: "default",
        red: "destructive",
        green: "success",
        yellow: "warning",
        orange: "warning",
        pink: "secondary",
        purple: "secondary",
        grey: "secondary",
        gray: "secondary", // Alternative spelling
        white: "outline-solid",
        black: "default",
    },

    // Kind to variant mapping
    kinds: {
        primary: "default",
        secondary: "secondary",
        tertiary: "ghost",
        custom: "default",
    },

    // Size mapping
    sizes: {
        tiny: "icon",
        small: "sm",
        medium: "default",
        large: "lg",
        huge: "lg",
    },

    // Shadow to additional classes
    shadows: ShadowsMapping,
} as const;

// Type helpers
export type FictoanButtonColor = keyof typeof fictoanButtonMapping.colors;
export type FictoanButtonKind = keyof typeof fictoanButtonMapping.kinds;
export type FictoanButtonSize = keyof typeof fictoanButtonMapping.sizes;
export type FictoanButtonShadow = keyof typeof fictoanButtonMapping.shadows;

// Fictoan InfoPanel Props (from Fictoan v0.45.5)
export interface FictoanInfoPanelProps {
    // Size/width (maps to custom CSS classes or side-specific sizing)
    width?: "tiny" | "small" | "medium" | "large" | "huge";

    // State management
    isOpen?: boolean;
    onCloseCallback?: () => void;
    closeOnClickOutside?: boolean;

    // Common Fictoan props that we might need to handle
    bgColor?: string;
    bgColour?: string;
    textColor?: string;
    textColour?: string;
    padding?: SpacingPaddingType;
    margin?: SpacingMarginType;
    shadow?: ShadowType;
    shape?: ShapeType;
    className?: string;
    classNames?: string[];

    // Common HTML props
    children?: React.ReactNode;
}

// Mapping from Fictoan InfoPanel to Sheet props
export const fictoanInfoPanelMapping = {
    // Width to custom classes or max-width
    widths: {
        tiny: "setu:max-w-xs", // ~320px
        small: "setu:max-w-sm", // ~384px
        medium: "setu:max-w-md", // ~448px
        large: "setu:max-w-lg", // ~512px
        huge: "setu:max-w-xl", // ~576px
    },

    // State mapping
    stateMapping: {
        isOpen: "open",
        onCloseCallback: "onOpenChange",
    },

    // Common spacing mapping
    spacing: SpacingPaddingMapping,

    // Shadow mapping
    shadows: ShadowsMapping,

    // Shape mapping
    shapes: ShapeMapping,
} as const;

// Type helpers
export type FictoanInfoPanelWidth = keyof typeof fictoanInfoPanelMapping.widths;
export type FictoanInfoPanelSpacing = SpacingPaddingType;
export type FictoanInfoPanelShadow = ShadowType;
export type FictoanInfoPanelShape = ShapeType;

// Export spacing mapping for easy access
export const fictoanSpacingMapping = fictoanInfoPanelMapping.spacing;
export type FictoanSpacing = SpacingPaddingType;

// Fictoan Card Props (from Fictoan v0.45.5)
export interface FictoanCardProps {
    // Common Fictoan props
    bgColor?: string;
    bgColour?: string;
    textColor?: string;
    textColour?: string;
    borderColor?: string;
    borderColour?: string;
    padding?: SpacingPaddingType;
    margin?: SpacingMarginType;
    shadow?: ShadowType;
    shape?: ShapeType;
    className?: string;
    classNames?: string[];
    children?: React.ReactNode;
}

// Fictoan InputField Props (from Fictoan v0.45.5)
export interface FictoanInputFieldProps {
    // Label props
    label?: string;
    labelColour?: string;

    // Input props
    type?: string;
    placeholder?: string;
    value?: string;
    defaultValue?: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;

    // Icon props
    iconLeft?: React.ReactElement;
    iconRight?: React.ReactElement;

    // Validation props
    helpText?: string;
    errorText?: string;
    validateThis?: boolean;
    valid?: boolean;
    invalid?: boolean;

    // Common Fictoan props
    bgColor?: string;
    bgColour?: string;
    textColor?: string;
    textColour?: string;
    borderColor?: string;
    borderColour?: string;
    padding?: SpacingPaddingType;
    margin?: SpacingMarginType;
    shadow?: ShadowType;
    shape?: "rounded" | "curved";
    className?: string;
    classNames?: string[];
    disabled?: boolean;
    required?: boolean;
}

// Fictoan Tooltip Props (from Fictoan v0.45.5)
export interface FictoanTooltipProps {
    label: string;
    position?: "top" | "bottom" | "left" | "right";
    children?: React.ReactNode;

    // Common Fictoan props (inherits from Card)
    bgColor?: string;
    bgColour?: string;
    textColor?: string;
    textColour?: string;
    padding?: SpacingPaddingType;
    shadow?: ShadowType;
    shape?: ShapeType;
    className?: string;
    classNames?: string[];
}

// Fictoan Badge Props (from Fictoan v0.45.5)
export interface FictoanBadgeProps {
    size?: SizeType;
    shape?: ShapeType;

    // Common Fictoan props
    bgColor?: string;
    bgColour?: string;
    textColor?: string;
    textColour?: string;
    borderColor?: string;
    borderColour?: string;
    padding?: SpacingPaddingType;
    margin?: SpacingMarginType;
    shadow?: ShadowType;
    className?: string;
    classNames?: string[];
    children?: React.ReactNode;
}

// Enhanced mapping schemas for other components
export const fictoanComponentMapping = {
    // Badge size mapping
    badgeSizes: {
        tiny: "setu:!text-xs setu:!px-1 setu:!py-0.5",
        small: "setu:!text-xs setu:!px-2 setu:!py-1",
        medium: "setu:!text-sm setu:!px-2.5 setu:!py-1.5",
        large: "setu:!text-base setu:!px-3 setu:!py-2",
        huge: "setu:!text-lg setu:!px-4 setu:!py-2.5",
    },

    // InputField validation states
    inputStates: {
        valid: "setu:border-green-500 setu:focus:border-green-500 setu:focus:ring-green-500",
        invalid: "setu:border-red-500 setu:focus:border-red-500 setu:focus:ring-red-500",
        default: "setu:border-input setu:focusborder-ring setu:focus:ring-ring",
    },

    // Tooltip positions
    tooltipPositions: {
        top: "bottom",
        bottom: "top",
        left: "right",
        right: "left",
    },

    // Common color mapping (for all components)
    colors: {
        blue: "blue-500",
        red: "red-500",
        green: "green-500",
        yellow: "yellow-500",
        orange: "orange-500",
        pink: "pink-500",
        purple: "purple-500",
        grey: "gray-500",
        gray: "gray-500",
        white: "white",
        black: "black",
    },
} as const;

export type TypographySpacingProps = {
    padding?: SpacingPaddingType;
    paddingTop?: SpacingPaddingType;
    paddingBottom?: SpacingPaddingType;
    paddingLeft?: SpacingPaddingType;
    paddingRight?: SpacingPaddingType;
    margin?: SpacingMarginType;
    marginTop?: SpacingMarginType;
    marginBottom?: SpacingMarginType;
    marginLeft?: SpacingMarginType;
    marginRight?: SpacingMarginType;
};

export const TypographySpacingPropsMapping = {
    padding: SpacingPaddingMapping,
    paddingTop: {
        none: "setu:!pt-0",
        nano: "setu:!pt-1",
        micro: "setu:!pt-2",
        tiny: "setu:!pt-3",
        small: "setu:!pt-4",
        medium: "setu:!pt-6",
        large: "setu:!pt-8",
        huge: "setu:!pt-12",
    },
    paddingBottom: {
        none: "setu:!pb-0",
        nano: "setu:!pb-1",
        micro: "setu:!pb-2",
        tiny: "setu:!pb-3",
        small: "setu:!pb-4",
        medium: "setu:!pb-6",
        large: "setu:!pb-8",
        huge: "setu:!pb-12",
    },
    paddingLeft: {
        none: "setu:!pl-0",
        nano: "setu:!pl-1",
        micro: "setu:!pl-2",
        tiny: "setu:!pl-3",
        small: "setu:!pl-4",
        medium: "setu:!pl-6",
        large: "setu:!pl-8",
        huge: "setu:!pl-12",
    },
    paddingRight: {
        none: "setu:!pr-0",
        nano: "setu:!pr-1",
        micro: "setu:!pr-2",
        tiny: "setu:!pr-3",
        small: "setu:!pr-4",
        medium: "setu:!pr-6",
        large: "setu:!pr-8",
        huge: "setu:!pr-12",
    },
    margin: SpacingMarginMapping,
    marginTop: {
        none: "setu:!mt-0",
        nano: "setu:!mt-1",
        micro: "setu:!mt-2",
        tiny: "setu:!mt-3",
        small: "setu:!mt-4",
        medium: "setu:!mt-6",
        large: "setu:!mt-8",
        huge: "setu:!mt-12",
    },
    marginBottom: {
        none: "setu:!mb-0",
        nano: "setu:!mb-1",
        micro: "setu:!mb-2",
        tiny: "setu:!mb-3",
        small: "setu:!mb-4",
        medium: "setu:!mb-6",
        large: "setu:!mb-8",
        huge: "setu:!mb-12",
    },
    marginLeft: {
        none: "setu:!ml-0",
        nano: "setu:!ml-1",
        micro: "setu:!ml-2",
        tiny: "setu:!ml-3",
        small: "setu:!ml-4",
        medium: "setu:!ml-6",
        large: "setu:!ml-8",
        huge: "setu:!ml-12",
    },
    marginRight: {
        none: "setu:!mr-0",
        nano: "setu:!mr-1",
        micro: "setu:!mr-2",
        tiny: "setu:!mr-3",
        small: "setu:!mr-4",
        medium: "setu:!mr-6",
        large: "setu:!mr-8",
        huge: "setu:!mr-12",
    },
} as const;

// Type helpers for all components
export type FictoanBadgeSize = keyof typeof fictoanComponentMapping.badgeSizes;
export type FictoanInputState = keyof typeof fictoanComponentMapping.inputStates;
export type FictoanTooltipPosition = keyof typeof fictoanComponentMapping.tooltipPositions;
export type FictoanColor = keyof typeof fictoanComponentMapping.colors;
export type ColorProps = `${TailwindColorsType}-${supportedShadesType}` | `${CustomColours}-${supportedShadesType}`;
